import { Link } from "react-router-dom";
import { WhatsAppCTA } from "@/components/WhatsApp";
import {
  Cog, Flame, Drill, Slice, Sparkles, CircleDot, ScanLine, CornerDownRight, SprayCan,
  ArrowRight, ShieldCheck, Clock3, Ruler, ChevronRight,
} from "lucide-react";

export const SERVICES = [
  {
    icon: Cog,
    slug: "centro-usinagem",
    title: "Usinagem CNC em Centro de Usinagem Vertical",
    desc: "Usinagem em centros verticais Romi e Eurostec para peças seriadas e protótipos com tolerâncias rigorosas.",
  },
  {
    icon: CircleDot,
    slug: "torneamento",
    title: "Torneamento",
    desc: "Torneamento de eixos, buchas, roscas e componentes cilíndricos, unitários ou em série.",
  },
  {
    icon: Sparkles,
    slug: "plasma",
    title: "Corte Plasma CNC",
    desc: "Corte plasma CNC de chapas de aço carbono, inox e alumínio com leitura direta de arquivos DXF.",
  },
  {
    icon: CornerDownRight,
    slug: "dobra",
    title: "Dobra de Chapas",
    desc: "Realizamos dobra de chapas metálicas com precisão, atendendo peças e componentes conforme as especificações de cada projeto.",
    highlight: "Até 2,0 mm",
  },
  {
    icon: Drill,
    slug: "fresamento",
    title: "Fresamento",
    desc: "Fresamento de precisão para canais, rasgos, cavidades e superfícies planas ou perfiladas.",
  },
  {
    icon: ScanLine,
    slug: "gravacao-laser",
    title: "Gravação a Laser",
    desc: "Gravação e marcação permanente em peças, códigos, números de série e logotipos.",
  },
  {
    icon: Flame,
    slug: "solda",
    title: "Solda MMA / MIG / TIG",
    desc: "Soldagem MMA, MIG e TIG em estruturas metálicas, conjuntos soldados e reparos industriais.",
  },
  {
    icon: Slice,
    slug: "serra-fita",
    title: "Corte em Serra Fita",
    desc: "Corte de barras, perfis e tubos em serra fita com esquadria e medidas exatas.",
  },
  {
    icon: SprayCan,
    slug: "jateamento",
    title: "Jateamento",
    desc: "Jateamento de peças e componentes metálicos para preparação e acabamento de superfícies, com orçamento que considera dimensões, faces e complexidade da peça.",
  },
];

const STATS = [
  { k: "10+", v: "máquinas em operação" },
  { k: "5.000+", v: "peças entregues" },
  { k: "±0,01", v: "mm de tolerância" },
  { k: "48h", v: "resposta de orçamento" },
];

const WHY = [
  {
    icon: Ruler,
    title: "Precisão garantida",
    desc: "Processos com controle dimensional e inspeção em cada etapa da fabricação.",
  },
  {
    icon: Clock3,
    title: "Prazo cumprido",
    desc: "Planejamento de produção rigoroso para entregar no prazo combinado.",
  },
  {
    icon: ShieldCheck,
    title: "Qualidade certificada",
    desc: "Matéria-prima rastreada e acabamento conforme especificação do projeto.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1764114235916-74de69e6851f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxDTkMlMjBtYWNoaW5pbmclMjBtZXRhbCUyMHdvcmtzaG9wfGVufDB8fHx8MTc4OTM1NDAwOXww&ixlib=rb-4.1.0&q=85"
          alt="Máquina de corte industrial trabalhando em chapa metálica"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
        <div className="absolute inset-0 grid-bg opacity-40" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-32 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-orange-500" />
              <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">
                usinagem cnc · corte · solda · fabricação
              </span>
            </div>
            <h1 className="display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight">
              Do desenho<br />
              à <span className="text-orange-500">peça perfeita</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
              Usinagem em centro vertical, torneamento, fresamento, corte plasma e laser,
              gravação laser, solda e serra fita. A Alpha Metais transforma seu projeto
              em peças metálicas com precisão, qualidade e prazo garantidos.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/painel/orcamentos/novo"
                data-testid="hero-quote-button"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-semibold uppercase tracking-wider px-7 py-3.5 rounded-sm transition-colors"
              >
                Solicitar orçamento <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/servicos"
                data-testid="hero-services-button"
                className="inline-flex items-center gap-2 border border-zinc-600 hover:border-orange-500 hover:text-orange-400 text-zinc-200 font-semibold uppercase tracking-wider px-7 py-3.5 rounded-sm transition-colors"
              >
                Nossos serviços
              </Link>
              <WhatsAppCTA message="Olá! Gostaria de solicitar um orçamento." testId="hero-whatsapp-cta" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-zinc-800 bg-zinc-900/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.v} className="py-10 px-4 text-center border-zinc-800 lg:border-l first:border-l-0" data-testid={`stat-${s.v.replace(/\W+/g, "-")}`}>
              <div className="display text-4xl sm:text-5xl font-extrabold text-orange-500">{s.k}</div>
              <div className="mt-1 text-xs sm:text-sm uppercase tracking-wider text-zinc-500">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-10 bg-orange-500" />
                <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">o que fazemos</span>
              </div>
              <h2 className="display text-4xl sm:text-5xl font-extrabold uppercase">Serviços</h2>
            </div>
            <Link
              to="/servicos"
              data-testid="services-see-all-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400 transition-colors"
            >
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/servicos#${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group bg-zinc-950 p-7 hover:bg-zinc-900 transition-colors"
              >
                <s.icon className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                <h3 className="display mt-4 text-xl font-bold uppercase tracking-wide leading-snug group-hover:text-orange-400 transition-colors">
                  {s.title}
                </h3>
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{s.desc}</p>
                {s.highlight && (
                  <span
                    data-testid={`service-highlight-${s.slug}`}
                    className="mt-3 inline-block border border-orange-500/60 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm"
                  >
                    {s.highlight}
                  </span>
                )}
              </Link>
            ))}
            <Link
              to="/painel/orcamentos/novo"
              data-testid="service-card-quote-cta"
              className="group bg-orange-600 hover:bg-orange-500 p-7 transition-colors flex flex-col justify-between"
            >
              <span className="display text-2xl font-bold uppercase leading-tight text-white">
                Peças sob desenho?<br />Fale com a gente.
              </span>
              <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-orange-100">
                Solicitar orçamento <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-24 bg-zinc-900/40 border-y border-zinc-800 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1508465818649-14a170138405?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxwbGFzbWElMjBjdXR0aW5nJTIwc3BhcmtzJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3ODkzNTQwMDl8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-10 bg-orange-500" />
              <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">por que a alpha</span>
            </div>
            <h2 className="display text-4xl sm:text-5xl font-extrabold uppercase leading-tight">
              Engenharia que se mede em <span className="text-orange-500">décimos de milímetro</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="card-tactical rounded-md p-7" data-testid={`why-${w.title.split(" ")[1] || w.title}`}>
                <w.icon className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                <h3 className="display mt-4 text-xl font-bold uppercase tracking-wide">{w.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-md border border-orange-900/50 bg-gradient-to-br from-zinc-900 to-zinc-950 p-10 sm:p-16 text-center scan-line">
            <h2 className="display text-3xl sm:text-5xl font-extrabold uppercase leading-tight">
              Tem um projeto em mãos?<br />
              <span className="text-orange-500">Envie seu desenho e receba um orçamento.</span>
            </h2>
            <p className="mt-5 text-zinc-400 max-w-xl mx-auto text-sm sm:text-base">
              Aceitamos arquivos DXF, PDF e STEP. Nossa equipe responde em até 48 horas úteis.
            </p>
            <Link
              to="/painel/orcamentos/novo"
              data-testid="cta-quote-button"
              className="mt-8 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-semibold uppercase tracking-wider px-8 py-4 rounded-sm transition-colors"
            >
              Solicitar orçamento <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
